import uiIcon from '@shared/ui-icon';
import 'web-animations-js';
import template from 'lodash-es/template';
import { cdn_url } from '@shared/cdn';
import PasswordlessForm from '@shared/passwordless-form';
import { T_ } from '@shared/translation';
import Modal from '@shared/ui-modal';
import trackingGtm from '@shared/tracking-gtm';
import {
  currencyList, budgetList, commitmentDurationList, defaultCurrency,
} from 'server-data/homepage-one-click-post-project';
import { getCSRFToken } from '@shared/cookie';
import AutofillClickableDropdown from '@shared/autofill-clickable-dropdown';
import ProjectFormBudgetDetails
  from '@shared/project-form-budget-details';
import createLocationField from './location-field';
import 'css/vendor/GoogleMapsAutocomplete.less';
import templateModalBody from './template-modal-body.html';
import contestSubcategories from './contest-subcategories';

// Select the correct currency based on default currency
const defaultCurrencyList = currencyList.map((currency) => {
  currency.selected = currency.id === defaultCurrency.id;
  return currency;
});

const oneClickPostFormEl = document.getElementById('one-click-post-form');
const oneClickAutofillContainerEl = document
  .getElementById('one-click-autofill-container');
const oneClickAutofillInputEl = document
  .getElementById('one-click-title-autofill');
const oneClickAutofillDropdownContainerEl = document
  .getElementById('one-click-search-dropdown-container');
const oneClickPostProjectJobs = document
  .getElementById('one-click-post-form-jobs');
const identifier = 'one-click-post-project-modal';
const passwordlessModal = new Modal('ocp-passwordless-funnel', {
  body: template(templateModalBody)({
    T_,
    identifier,
    cdn_url,
    currencies: defaultCurrencyList,
    budgets: budgetList,
    commitmentDurations: commitmentDurationList,
    uiIcon,
  }),
}, {
  backdropClose: false,
});

const passwordlessForm = new PasswordlessForm({
  onSignupModeActivated: () => {
    // Show T&C text
    const signupTncField = document
      .getElementById(`${identifier}-signup-tnc-copy`);
    if (signupTncField) {
      signupTncField.classList.remove('is-hidden');
    }
  },
  onSignupModeDeactivated: () => {
    // Hide T&C text
    const signupTncField = document
      .getElementById(`${identifier}-signup-tnc-copy`);
    if (signupTncField) {
      signupTncField.classList.add('is-hidden');
    }
  },
  submissionCallback: (form) => {
    // Replace the `action` of form to submit project instead of redirecting to
    // /post-project before form submission
    oneClickPostFormEl.setAttribute('action', '/buyers/onverifycreate.php');
    oneClickPostFormEl.setAttribute('method', 'POST');

    // When the form is submitted, cancel it and submit the one click PPP
    // form instead.
    // Move all the data in the modal form to the one click PPP form.
    oneClickPostFormEl.email.value = form.email.value;
    oneClickPostFormEl.newusername.value = form.newusername.value;
    oneClickPostFormEl.newuserpasswd.value = form.newuserpasswd.value;
    oneClickPostFormEl.currency.value = form.currency.value;
    oneClickPostFormEl.budget.value = form.budget.value;
    oneClickPostFormEl.commitment.value = form.commitment.value;
    oneClickPostFormEl.duration.value = form.duration.value;
    oneClickPostFormEl.csrf_token.value = getCSRFToken();
    // NOTE: special handling of radio inputs, to fix bug in safari and IE as
    // they don't support RadioNodeList interface yet

    oneClickPostFormEl.budget_set.value = Array.from(form.budgetSet)
    .find(el => el.checked).value;

    oneClickPostFormEl.budgetPeriod.value = Array.from(form.budget_period)
      .find(el => el.checked).value;

    trackingGtm.pushEvent(trackingGtm.ONE_CLICK_PROJECT_POSTED);

    function submit(res) {
      _t.push(['user_action', {
        section: 'HomePage',
        subsection: 'OneClick',
        label: 'TriedFormSubmit',
      }]);

      if (res) {
        oneClickPostFormEl.submit();
        passwordlessForm.submitEl.textContent = T_('Submitting...');
        passwordlessForm.submitEl.setAttribute('disabled', 'disabled');
      } else {
        passwordlessForm.showLoginError();
      }
    }

    if (passwordlessForm.isLoginMode()) {
      _t.push(['user_action', {
        section: 'HomePage',
        subsection: 'OneClick',
        label: 'TriedLoginThroughAjax',
      }]);

      const formData = new FormData();
      formData.append('username', form.email.value);
      formData.append('passwd', form.newuserpasswd.value);
      formData.append('csrf_token', getCSRFToken());
      fetch('/ajax/login.php', {
        method: 'POST',
        credentials: 'same-origin',
        body: formData,
      })
        .then(response => response.json())
        .then(data => submit(data.status === 'success'));
    } else {
      submit(true);
    }
  },
});

// Bind dom elements
const budgetToggleFalse = document
  .getElementById(`${identifier}-budget-false`);
const budgetToggleTrue = document
  .getElementById(`${identifier}-budget-true`);
const currencySelectorEl = document.getElementById(`${identifier}-currency`);
const fixedBudgetPeriodRadioEl = document
  .getElementById(`${identifier}-budgetperiod-fixed`);
const hourlyBudgetPeriodRadioEl = document
  .getElementById(`${identifier}-budgetperiod-hourly`);
const budgetSelectorEl = document
  .getElementById(`${identifier}-budget`);
const budgetDetailsContainerEl = document
  .getElementById(`${identifier}-budget-details`);
const jobTypeCardEls = document
  .querySelectorAll(`[data-target="${identifier}-job-type-card"]`);
const jobTypeProjectEl = document
  .getElementById(`${identifier}-job-type-project`);
const jobTypeContestEl = document
  .getElementById(`${identifier}-job-type-contest`);
const jobTypeSpecialCopy = document
  .getElementById(`${identifier}-job-type-special-copy`);
const jobNameEl = document.getElementById(`${identifier}-job-name`);

passwordlessForm.initForm(identifier);
const locationField = createLocationField(
  document.getElementById('project-location'),
  document.getElementById('location_field'),
  document.getElementById('user_current_location'),
  oneClickPostFormEl,
  passwordlessForm.validator);

// Create new ProjectFormBudgetDetails component to handle budget interactions
const budgetDetailsComponent = new ProjectFormBudgetDetails({
  currencySelectorEl,
  budgetSelectorEl,
  supportHourlyBudgetPeriod: true,
  fixedPeriodRadioEl: fixedBudgetPeriodRadioEl,
  hourlyPeriodRadioEl: hourlyBudgetPeriodRadioEl,
});

// Setup clickable dropdown autofill
const autofillComponent = new AutofillClickableDropdown({
  containerEl: oneClickAutofillContainerEl,
  inputEl: oneClickAutofillInputEl,
  dropdownEl: oneClickAutofillDropdownContainerEl,
  autofillList: [],
  autofillSearchOptions: { keys: ['name'], threshold: 0.5 },
  autofillDropdownOptions: {
    showTop: 4,
    prioritiseByField: 'priority',
    uniqueWith: (a, b) =>
      a.name.trim().toLowerCase() === b.name.trim().toLowerCase()
    ,
  },
  onListItemSelectCallback(entry, itemId, event) {
    _t.push(['user_action', {
      section: 'HomePage',
      subsection: 'OneClick',
      label: 'SelectedFromAutofillDropdown',
      reference: 'itemId',
      reference_id: itemId,
    }]);
    // Assume the user is prepared to go through the funnel when they select
    // one list item
    oneClickPostFormSubmitHandler(event);
  },
});

// Fetch Autofill list asynchronously
AutofillClickableDropdown.fetchAutofillList().then((res) => {
  if (res.status !== 'success') {
    return;
  }
  autofillComponent.setAutofillList(res.autofills);
});

// NOTE: We do this because the default value of loaded currency in the DOM
// might be incorrect, due to Varnish caching other people's pages
ProjectFormBudgetDetails.fetchIpBasedCurrency().then((res) => {
  if (res.status !== 'success') {
    return;
  }
  budgetDetailsComponent.selectCurrency(res.currency.id);
});

budgetToggleTrue.addEventListener('change', () => {
  _t.push(['user_action', {
    section: 'HomePage',
    subsection: 'OneClick',
    label: 'ChangedBudgetSetToggle',
    name: 'budget_set',
  }]);
  budgetDetailsContainerEl.classList.remove('is-hidden');
});
budgetToggleFalse.addEventListener('change', () => {
  _t.push(['user_action', {
    section: 'HomePage',
    subsection: 'OneClick',
    label: 'ChangedBudgetSetToggle',
    name: 'budget_unset',
  }]);
  budgetDetailsContainerEl.classList.add('is-hidden');
});

// Bind events for handling the job type radio selector
Array.from(jobTypeCardEls).forEach((card) => {
  card.addEventListener('click', () => {
    // Check the radio button straight away
    selectJobType(card);
  });
});

oneClickPostFormEl.addEventListener('submit', oneClickPostFormSubmitHandler);

/**
 * Handles the select state of Job Type cards
 * @param  {Node} selectedCard Card element to set selected state
 */
function selectJobType(selectedCard) {
  const cardRadioEl =
    selectedCard.querySelector(`[data-target="${identifier}-job-type-radio"]`);

  Array.from(jobTypeCardEls).forEach((card) => {
    card.classList.remove('is-selected');
  });

  selectedCard.classList.add('is-selected');
  cardRadioEl.checked = true;
  onMatchedJobType(cardRadioEl.value);
}

function oneClickPostFormSubmitHandler(event) {
  // Put skills value to DOM if project title matches anything
  const matchingEntry = getMatchingAutofillEntryFromTitle();

  // Record via QTS when user tries to submit form without matching entry
  if (!matchingEntry) {
    _t.push(['user_action', {
      section: 'HomePage',
      subsection: 'OneClick',
      label: 'MatchlessTitleEntry',
      name: oneClickPostFormEl.project_name.value,
    }]);
  }

  // If user has enough login/signup credentials, we take it that they have
  // when through the modal. submit the one click form straight away
  if (hasEnoughCredentialsFromPasswordlessForm()) {
    return;
  }

  // User does not have enough login/signup creds, signaling they haven't gone
  // through the modal. Prepare and open the modal
  _t.push(['user_action', {
    section: 'HomePage',
    subsection: 'OneClick',
    label: 'OneClickModalOpened',
  }]);

  // Fill in the result to the input box
  jobNameEl.textContent = oneClickAutofillInputEl.value;

  // Reset the sliding panes in modal
  passwordlessForm.reset();
  if (passwordlessForm.panes.length > 1) {
    passwordlessForm.panes[0].removeAttribute('data-pane-disabled');
    passwordlessForm.panes[1].removeAttribute('data-pane-backnav-disabled');
  }

  // Render jobs/skills list to hidden fields
  const jobs = matchingEntry && matchingEntry.jobs ? matchingEntry.jobs : [];
  oneClickPostProjectJobs.innerHTML = template(
    '<% for (var i = 0; i < list.length; i++) { %>' +
      '<input type="hidden" value="on" name="job_<%- list[i] %>">' +
    '<% } %>',
  )({ list: jobs });

  // Render category to hidden field too if we have a category
  if (matchingEntry && matchingEntry.categoryId) {
    oneClickPostFormEl.skill_category.value = matchingEntry.categoryId;
  }

  // Open the modal
  passwordlessModal.show();
  onRenderDynamicForm(matchingEntry);
  event.preventDefault();
}

function hasEnoughCredentialsFromPasswordlessForm() {
  const emailFilled = !!oneClickPostFormEl.email.value;
  const usernameFilled = !!oneClickPostFormEl.newusername.value;
  const passwordFilled = !!oneClickPostFormEl.newuserpasswd.value;
  return emailFilled && (usernameFilled || passwordFilled);
}

/**
 * Processes the initial input button combo form on the hero section.
 * @return {Object} The matching data. {Boolean} false if there aren't any.
 */
function getMatchingAutofillEntryFromTitle() {
  const projectTitle = oneClickAutofillInputEl.value;
  const matches = autofillComponent.getAutofillList().filter(item =>
    item.name.trim().toLowerCase() === projectTitle.trim().toLowerCase(),
  );

  // Fill skills and detect contest subcategory if:
  // 1. we have matches,
  // 2. the first match has skills attached to it
  let matchingData = false;
  if (matches.length && matches[0].jobs) {
    matchingData = matches[0];
  }
  return matchingData;
}

function onMatchedJobType(jobType) {
  _t.push(['user_action', {
    section: 'HomePage',
    subsection: 'OneClick',
    label: 'OnSelectedJobTypeCard',
    name: jobType,
  }]);

  const nextButtonEl = document
    .getElementById(`${identifier}-next-${jobType}`);
  const defaultNextButtonEl = document
    .getElementById(`${identifier}-next`);

  // Disable other Next buttons before we continue
  Array.from(document.querySelectorAll('[data-next-other-funnel]'))
    .forEach(buttonEl => buttonEl.classList.add('is-hidden'));

  if (nextButtonEl && defaultNextButtonEl) {
    if (!nextButtonEl.href.match(/\?/)) {
      nextButtonEl.href += '?';
    }
    let prefillParam = 'project_name';
    if (jobType === 'contest') {
      prefillParam = 'contest_name';
    }
    const name = oneClickPostFormEl.project_name.value;
    nextButtonEl.href += `&${prefillParam}=${name}`;
    // If this is a card that'll take to another page, hide the Next button
    // and enable the fake "Next" button that will take the user to another
    // funnel.
    defaultNextButtonEl.classList.add('is-hidden');
    nextButtonEl.classList.remove('is-hidden');
    nextButtonEl.focus();
  } else if (defaultNextButtonEl) {
    defaultNextButtonEl.classList.remove('is-hidden');
    defaultNextButtonEl.focus();
  }
}

// Note: use only when there is more than one pane
function skipFirstPane() {
  passwordlessForm.panes[0].setAttribute('data-pane-disabled', 'disabled');
  passwordlessForm.panes[1]
    .setAttribute('data-pane-backnav-disabled', 'disabled');
  passwordlessForm.switchPanes(2, false);
}

function onRenderDynamicForm(matchingAutofillEntry) {
  // Hide all optional job type before processing which one to show.
  jobTypeContestEl.classList.add('is-hidden');
  jobTypeSpecialCopy.classList.add('is-hidden');
  locationField.hide();

  // HACK: Reset to correct currency if we rerender the template or something
  const currentCurrencyId = budgetDetailsComponent.state.currency.id;
  budgetDetailsComponent.selectCurrency(currentCurrencyId);

  if (
      matchingAutofillEntry.subcategory &&
      contestSubcategories[matchingAutofillEntry.subcategory]
  ) {
    // The subcategory is a contest-worthy one. Let's prompt for the user if
    // they prefer contests instead.
    jobTypeContestEl.classList.remove('is-hidden');
    jobTypeSpecialCopy.classList.remove('is-hidden');
    passwordlessForm.switchPanes(1, false);
    selectJobType(jobTypeContestEl);
  } else if (matchingAutofillEntry.isLocalJob) {
    locationField.show();
    skipFirstPane();
    onMatchedJobType('local');
  } else {
    // Nothing matching? Move to the second slide straight away
    selectJobType(jobTypeProjectEl);
    // Disable first slide, slide to next
    if (passwordlessForm.panes.length > 1) {
      skipFirstPane();
    }
  }
}

const oneClickSubmitButton = document.getElementById('hero-hire-button');
const clickEls = Array.from(document.querySelectorAll('[data-one-click-name]'));
if (clickEls) {
  clickEls.forEach((el) => {
    const oneClickValue = el.getAttribute('data-one-click-name');
    el.addEventListener('click', () => {
      _t.push(['user_action', {
        section: 'HomePage',
        subsection: 'OneClick',
        label: 'TriggeredOneClickFromOtherParts',
        name: oneClickValue,
      }]);
      oneClickAutofillInputEl.value = oneClickValue;
      oneClickSubmitButton.click();
    });
  });
}

if (window.performance && window.performance.mark) {
  window.performance.mark('homepage_one_click_ready');
}
